import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Turnstile } from '@marsidev/react-turnstile';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const WaitlistSection = () => {
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !turnstileToken) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/waitlist`, {
        email: email,
        turnstile_token: turnstileToken,
      });

      if (response.data.success) {
        setSubmittedEmail(email);
        setIsSuccess(true);
      }
    } catch (err) {
      console.error('Waitlist error:', err);
      const detail = err.response?.data?.detail;
      const errorMessage =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : 'Something went wrong. Please try again.';
      setError(errorMessage);
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <section id="waitlist" className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-8 rounded-3xl border border-black/5 shadow-lg"
              style={{ background: 'var(--accent-wash)' }}
            >
              <CheckCircle size={48} className="mx-auto mb-4" style={{ color: 'var(--accent-text)' }} />
              <h3 className="heading-3 mb-2">You're on the list!</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Thanks for joining, <strong>{submittedEmail}</strong>. We'll notify you when Crew Up launches in South Africa.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="waitlist" className="section-padding bg-white">
      <div className="container mx-auto">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <span
              className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4"
              style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}
            >
              Early Access
            </span>
            <h2 className="heading-2 mb-4">Be the first to know</h2>
            <p className="body-large mb-8">
              Be the first to know when Crew Up launches in South Africa.
              Join our waitlist for early access and exclusive updates.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="relative">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="h-14 px-6 text-base rounded-full border-black/10 focus:border-[#8FEC78] focus:ring-[#8FEC78]"
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary h-14 px-8 gap-2"
                  disabled={isLoading || !turnstileToken}
                >
                  {isLoading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      Get Early Access
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>

              <div className="flex justify-center mt-4">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                />
              </div>

              {error && (
                <p className="text-sm text-red-600 mt-3 text-center">{error}</p>
              )}
            </form>

            <p className="text-center mt-4 text-sm" style={{ color: 'var(--text-muted)' }}>
              No spam, ever. We'll only email you about Crew Up updates.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WaitlistSection;
